import { Component, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faCalendarPlus } from '@fortawesome/free-regular-svg-icons';
import { faCalendar, faCalendarCheck, faChevronLeft, faChevronRight, faCheck, faList, faRefresh, faRepeat, faUsers, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Observable, of, BehaviorSubject, merge } from 'rxjs';
import { map, shareReplay, switchMap, catchError, startWith } from 'rxjs/operators';
import { BotGuild, Event, GuildOverview } from 'src/app/models/bot/guild';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import { GuildService } from 'src/app/services/guild.service';
import { UserPreferencesService } from 'src/app/services/user-preferences.service';

/** Events grouped by date key (YYYY-MM-DD) for calendar display */
export interface EventsByDate {
  [dateKey: string]: Event[];
}

@Component({
  selector: 'app-overview-card',
  templateUrl: './overview-card.component.html',
  styleUrls: [
    '../shared/styles/guild-dashboard-shared.css',
    './overview-card.component.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewCardComponent implements OnInit {
  faIcons = {
    users: faUsers,
    calendarCheck: faCalendarCheck,
    calendar: faCalendar,
    chevronLeft: faChevronLeft,
    chevronRight: faChevronRight,
    cross: faXmark,
    check: faCheck,
    list: faList,
    calendaPlus: faCalendarPlus,
    repeat: faRepeat,
    refresh: faRefresh,
  };

  @Input() overview$: Observable<GuildOverview>;
  guildId: string;
  guild: BotGuild;
  viewMode: 'list' | 'calendar';
  calendarDate: Date = new Date();
  eventsByDate$: Observable<EventsByDate>;
  googleEventsByDate$: Observable<Record<string, Array<{ title: string; start: string; end?: string }>>>;
  conflicts$: Observable<Record<string, Array<{ title: string; start: string; end?: string }>>>;
  private calendarDate$ = new BehaviorSubject<Date>(new Date());
  private viewMode$ = new BehaviorSubject<'list' | 'calendar'>('list');
  /** Multi-month cache: monthKey -> days. Kept when switching months for instant display. */
  private calendarDaysCache: Record<string, { date: Date; dateKey: string; isCurrentMonth: boolean; isPast: boolean }[]> = {};
  /** Cache for Google Calendar events per month. Stale-while-revalidate: show cached first, then update when API returns. */
  private googleEventsCache: Record<string, Record<string, Array<{ title: string; start: string; end?: string }>>> = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private botService: BotResourceService,
    private guildService: GuildService,
    private userPrefs: UserPreferencesService,
    private cdr: ChangeDetectorRef,
  ) {
    this.viewMode = this.userPrefs.eventsViewMode;
    this.viewMode$.next(this.viewMode);
  }

  ngOnInit() {
    this.guildId = this.route.parent.snapshot.paramMap.get('id');

    this.overview$ = this.botService.getGuildOverview(this.guildId).pipe(
      shareReplay(1)
    );

    this.eventsByDate$ = this.overview$.pipe(
      map((overview) => this.groupEventsByDate(overview?.upcoming_events ?? []))
    );

    this.conflicts$ = this.botService.getCalendarConflicts(this.guildId).pipe(
      map((r) => r.conflicts || {}),
      catchError(() => of({})),
      startWith({}),
      shareReplay(1)
    );

    this.googleEventsByDate$ = this.viewMode$.pipe(
      switchMap((mode) =>
        mode === 'calendar'
          ? this.calendarDate$.pipe(
              switchMap((calDate) => {
                const monthKey = `${calDate.getFullYear()}-${calDate.getMonth()}`;
                const cached = this.googleEventsCache[monthKey];
                return this.botService.getGoogleCalendarStatus().pipe(
                  switchMap((s) => {
                    if (!s.connected) return of({});
                    const year = calDate.getFullYear();
                    const month = calDate.getMonth();
                    const timeMin = new Date(year, month, 1).toISOString();
                    const timeMax = new Date(year, month + 1, 0, 23, 59, 59).toISOString();
                    const apiCall$ = this.botService.getGoogleCalendarEvents(timeMin, timeMax).pipe(
                      map((r) => {
                        const result = this.groupGoogleEventsByDate(r.events || []);
                        this.googleEventsCache[monthKey] = result;
                        return result;
                      }),
                      catchError(() => of(cached || {}))
                    );
                    if (cached) {
                      return merge(of(cached), apiCall$);
                    }
                    return apiCall$.pipe(startWith({}));
                  })
                );
              }),
              shareReplay(1)
            )
          : of({})
      ),
      shareReplay(1)
    );

    this.guildService.getGuildData(this.guildId).subscribe((guild) => {
      this.guild = guild;
      this.cdr.markForCheck();
    });
  }

  groupGoogleEventsByDate(events: Array<{ title: string; start: string; end?: string }>): Record<string, Array<{ title: string; start: string; end?: string }>> {
    const todayKey = this.getTodayKey();
    const byDate: Record<string, Array<{ title: string; start: string; end?: string }>> = {};
    for (const e of events) {
      const d = new Date(e.start);
      if (isNaN(d.getTime())) continue;
      const key = this.toDateKey(d);
      if (key >= todayKey) {
        if (!byDate[key]) byDate[key] = [];
        byDate[key].push(e);
      }
    }
    return byDate;
  }

  getGoogleEventTime(e: { start: string }): string {
    const d = new Date(e.start);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  /** Get possible conflict lookup keys - backend uses str(message_id) or title */
  private getConflictKeys(event: Event): string[] {
    const keys: string[] = [];
    const mid = event.message_id ?? event.id;
    if (mid != null && String(mid).trim()) keys.push(String(mid).trim());
    if (event.title?.trim()) keys.push(event.title.trim());
    return keys;
  }

  hasConflict(event: Event, conflicts: Record<string, unknown[]>): boolean {
    if (!conflicts) return false;
    for (const key of this.getConflictKeys(event)) {
      const list = conflicts[key];
      if (list && Array.isArray(list) && list.length > 0) return true;
    }
    return false;
  }

  getConflictList(event: Event, conflicts: Record<string, Array<{ title: string }>>): string {
    if (!conflicts) return '';
    for (const key of this.getConflictKeys(event)) {
      const list = conflicts[key];
      if (list?.length) return list.map((c) => c.title).join(', ');
    }
    return '';
  }

  /** Get today's date key (YYYY-MM-DD) for filtering */
  getTodayKey(): string {
    return this.toDateKey(new Date());
  }

  /** Group events by date key for calendar placement; only includes events from today onward */
  groupEventsByDate(events: Event[]): EventsByDate {
    const todayKey = this.getTodayKey();
    const byDate: EventsByDate = {};
    for (const e of events) {
      const key = this.getEventDateKey(e);
      if (key && key >= todayKey) {
        if (!byDate[key]) byDate[key] = [];
        byDate[key].push(e);
      }
    }
    return byDate;
  }

  /** Extract YYYY-MM-DD from event start (ISO or similar) */
  getEventDateKey(event: Event): string | null {
    const raw = event.start ?? event.date;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Format event time for display (e.g. "6:00 PM") */
  getEventTime(event: Event): string {
    const raw = event.start ?? event.date;
    if (!raw) return '';
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  /** Truncate title for calendar cell */
  truncateTitle(title: string, maxLen = 22): string {
    if (!title) return '';
    return title.length <= maxLen ? title : title.slice(0, maxLen - 3) + '...';
  }

  setViewMode(mode: 'list' | 'calendar') {
    this.viewMode = mode;
    this.userPrefs.eventsViewMode = mode;
    this.viewMode$.next(mode);
    this.cdr.markForCheck();
  }

  refreshOverview() {
    this.overview$ = this.botService.getGuildOverview(this.guildId).pipe(
      shareReplay(1)
    );
    this.eventsByDate$ = this.overview$.pipe(
      map((overview) => this.groupEventsByDate(overview?.upcoming_events ?? []))
    );
    this.conflicts$ = this.botService.getCalendarConflicts(this.guildId).pipe(
      map((r) => r.conflicts || {}),
      catchError(() => of({})),
      shareReplay(1)
    );
    this.cdr.markForCheck();
  }

  prevMonth() {
    this.calendarDate = new Date(
      this.calendarDate.getFullYear(),
      this.calendarDate.getMonth() - 1,
      1
    );
    this.calendarDate$.next(this.calendarDate);
    this.cdr.markForCheck();
  }

  nextMonth() {
    this.calendarDate = new Date(
      this.calendarDate.getFullYear(),
      this.calendarDate.getMonth() + 1,
      1
    );
    this.calendarDate$.next(this.calendarDate);
    this.cdr.markForCheck();
  }

  getCalendarMonthLabel(): string {
    return this.calendarDate.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  /** Get days for the calendar grid (including padding for alignment). Multi-month cache for instant display when switching months. */
  getCalendarDays(): { date: Date; dateKey: string; isCurrentMonth: boolean; isPast: boolean }[] {
    const cacheKey = `${this.calendarDate.getFullYear()}-${this.calendarDate.getMonth()}-${this.getTodayKey()}`;
    const cached = this.calendarDaysCache[cacheKey];
    if (cached) {
      return cached;
    }
    const todayKey = this.getTodayKey();
    const year = this.calendarDate.getFullYear();
    const month = this.calendarDate.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startPad = first.getDay();
    const endPad = 6 - last.getDay();
    const days: { date: Date; dateKey: string; isCurrentMonth: boolean; isPast: boolean }[] = [];

    for (let i = 0; i < startPad; i++) {
      const d = new Date(year, month, 1 - (startPad - i));
      const dateKey = this.toDateKey(d);
      days.push({
        date: d,
        dateKey,
        isCurrentMonth: false,
        isPast: dateKey < todayKey,
      });
    }
    for (let d = 1; d <= last.getDate(); d++) {
      const date = new Date(year, month, d);
      const dateKey = this.toDateKey(date);
      days.push({
        date,
        dateKey,
        isCurrentMonth: true,
        isPast: dateKey < todayKey,
      });
    }
    for (let i = 1; i <= endPad; i++) {
      const d = new Date(year, month + 1, i);
      const dateKey = this.toDateKey(d);
      days.push({
        date: d,
        dateKey,
        isCurrentMonth: false,
        isPast: dateKey < todayKey,
      });
    }
    this.calendarDaysCache[cacheKey] = days;
    return days;
  }

  trackByDateKey(_index: number, day: { dateKey: string }): string {
    return day.dateKey;
  }

  toDateKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  onDayClick(dateKey: string) {
    if (!this.guild?.organizer && !this.guild?.owner) return;
    this.router.navigate(['/guilds', this.guildId, 'event', 'create'], {
      queryParams: { date: dateKey, mode: this.userPrefs.eventCreationMode },
    });
  }

  navigateToNewSchedule() {
    this.router.navigate(['/guilds', this.guildId, 'event', 'create'], {
      queryParams: { mode: this.userPrefs.eventCreationMode },
    });
  }

  navigateToPremium() {
    this.router.navigate(['/guilds', this.guildId, 'premium']);
  }

  onDayWrapperClick(day: any) {
    /* Only trigger if it's the current month, not past, and user has permissions */
    if (day.isCurrentMonth && !day.isPast && (this.guild?.organizer || this.guild?.owner)) {
      this.onDayClick(day.dateKey);
    }
  }
}
